import React, { useState } from "react";
import JsPDF from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import moment from "moment";
import Swal from "sweetalert2";
import { LinkedNetsuiteProductCourseList } from "../../../../services/ProgramService";
import {
    getCampusText,
    getMethodText,
    getReadiness,
    isVersionActive,
} from "../../../../utils/LinkedProgrammeUtils";
import { TABLE_DATE_FORMAT } from "../../../../utils/Constants";
import {
    StyledExportGroup,
    StyledExportButton,
} from "../../../library/BulkToolbarButtons";

/**
 * Builds the export column headers, switching between the standard and
 * QA column sets and dropping "Version" when versioning is disabled.
 * @param {Object} params
 * @param {boolean} params.QAview Whether the QA column set should be used.
 * @param {boolean} params.isVersionEnabled Whether the "Version" column is included.
 * @returns {string[]} Ordered column header names.
 */
function getHeaders({
    QAview,
    isVersionEnabled,
}) {
    const standardHeaders = [
        "Name",
        "Campus",
        "Delivery Method",
        "Version",
        "Intake",
        "Start Date",
        "End Date",
        "Grades",
        "Students",
    ];

    const qaHeaders = [
        "Name",
        "Campus",
        "Delivery Method",
        "Version",
        "Intake",
        "Module Readiness",
        "Grades",
        "Students",
    ];

    const headers = QAview
        ? qaHeaders
        : standardHeaders;

    return isVersionEnabled
        ? headers
        : headers.filter(
                (column) => column !== "Version"
            );
}

/**
 * Formats raw linked-programme rows into export-ready objects, resolving
 * campus/method labels, the locked/unlocked grade text, and the QA-only
 * module readiness column.
 * @param {Object} params
 * @param {Array} params.rows Raw linked-programme rows from the API.
 * @param {boolean} params.QAview Whether to populate the "Module Readiness" column.
 * @param {boolean} params.isVersionEnabled Whether to include the "Version" column.
 * @returns {Object[]} One export-ready row object per input row.
 */
function buildRows({
    rows,
    QAview,
    isVersionEnabled,
}) {
    return rows.map((row) => {
        const campusText = getCampusText(
            row?.campus
        );

        const methodText = getMethodText(
            row?.type
        );

        const locked =
            Number(row?.isGradeLocked) === 1; // 1 = grades locked (see programme isGradeLocked flag)

        const versionValue = isVersionActive(row)
            ? row?.pmc_version_name ||
                row?.version_name ||
                "Active Version"
            : row?.version_name || "-";

        const base = {
            Name:
                row?.name ||
                row?.programme_name ||
                row?.qualification_name ||
                "-",
            Campus: campusText,
            "Delivery Method": methodText,
            Version: versionValue,
            Intake:
                row?.intakes || "-",
            Grades: locked ? "Locked" : "Unlocked",
            Students:
                row?.total_student ?? "-",
        };

        if (QAview) {
            const readiness = getReadiness(row);

            base["Module Readiness"] =
                `${readiness.readyCount}/${readiness.total}`;
        } else {
            base["Start Date"] =
                row?.start
                ? moment.unix(row.start).format(TABLE_DATE_FORMAT)
                : "-";
            base["End Date"] =
                row?.end
                ? moment.unix(row.end).format(TABLE_DATE_FORMAT)
                : "-";
        }

        if (!isVersionEnabled) {
            delete base.Version;
        }

        return base;
    });
}

/**
 * Triggers a browser download of the given rows as a CSV file.
 * @param {Object[]} formattedRows Export-ready rows.
 * @param {string[]} headers Column headers, in display order.
 * @param {string} fileName Base file name (without extension).
 */
function downloadCsv(
    formattedRows,
    headers,
    fileName
) {
    const csv = Papa.unparse({
        fields: headers,
        data: formattedRows.map((row) => headers.map((header) => row[header] ?? "-")),
    });
    const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
    });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = href;
    anchor.download = `${fileName}.csv`;
    document.body.appendChild(anchor); // Append to body for Firefox compatibility
    anchor.click();
    document.body.removeChild(anchor);

    setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        URL.revokeObjectURL(href);
    }, 1000);
}

/**
 * Triggers a browser download of the given rows as an XLSX workbook.
 * @param {Object[]} formattedRows Export-ready rows.
 * @param {string[]} headers Column headers, in display order.
 * @param {string} fileName Base file name (without extension).
 */
function downloadXlsx(
    formattedRows,
    headers,
    fileName
) {
    const worksheet =
        XLSX.utils.json_to_sheet(formattedRows, { header: headers });
    const workbook =
        XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Linked Programmes"
    );

    XLSX.writeFile(
        workbook,
        `${fileName}.xlsx`
    );
}

/**
 * Triggers a browser download of the given rows as a PDF table.
 * @param {Object} params
 * @param {Object[]} params.formattedRows Export-ready rows.
 * @param {string[]} params.headers Column headers, in display order.
 * @param {string} params.fileName Base file name (without extension).
 */
function downloadPdf({
    formattedRows,
    headers,
    fileName,
}) {
    const pdfDocument = new JsPDF({
        orientation: "landscape",
    });

    pdfDocument.autoTable({
        head: [headers],
        body: formattedRows.map((row) =>
            headers.map((header) => row[header] ?? "-")
        ),
        styles: {
            fontSize: 8,
        },
    });

    pdfDocument.save(`${fileName}.pdf`);
}

/**
 * Export buttons (Excel, CSV, PDF) for the linked programmes list — fetches the full, unfiltered-by-page result set for the current filters before downloading it in the chosen format.
 * @param {boolean} isProcessing Whether a parent action is in flight (disables the buttons).
 * @param {number} totalRows Total row count for the current filters; `perPage` is the fallback page size when it isn't available.
 * @param {string} sortKey Column key to sort exported rows by, with `sortOrder` for direction.
 * @param {{arr: Array}} campus Selected filter state for `campus`, `intake`, `delivery`, `gradesLocked` and `version`.
 * @param {Function} [onError] Called with the error on export failure.
 * Also: id, detail, QAview, isVersionEnabled, search (self-explanatory).
 */
function LinkedProgrammeExport({
    id,
    detail,
    QAview,
    isVersionEnabled,
    isProcessing,
    totalRows,
    perPage,
    sortKey,
    sortOrder,
    search,
    campus,
    intake,
    delivery,
    gradesLocked,
    version,
    onError,
}) {
    const [isExporting, setIsExporting] = useState(false);

    const exportData = async (
        fileType,
        fileName
    ) => {
        setIsExporting(true);

        try {
            const response =
                await LinkedNetsuiteProductCourseList({
                    programme_id: id,
                    netsuite_product_id:
                        detail?.netsuite_product,
                    isAssigned: 1,
                    page: 1,
                    limit: totalRows || perPage,
                    key: sortKey,
                    sort: sortOrder,
                    search,
                    viaCampus: campus.arr,
                    intake: intake.arr,
                    viaLearnType: delivery.arr,
                    viaCourseType: [],
                    viaGradesLocked: gradesLocked.arr,
                    viaVersion: version.arr,
                    viaModule: [],
                });

            const rows =
                response?.data?.data?.data || [];

            const formattedRows = buildRows({
                rows,
                QAview,
                isVersionEnabled,
            });

            const headers = getHeaders({
                QAview,
                isVersionEnabled,
            });

            if (fileType === "csv") {
                downloadCsv(formattedRows, headers, fileName);
                return;
            }

            if (fileType === "xlsx") {
                downloadXlsx(formattedRows, headers, fileName);
                return;
            }

            if (fileType === "pdf") {
                downloadPdf({
                    formattedRows,
                    headers,
                    fileName,
                });
            }
        } catch (error) {
            console.log(
                "LinkedProgrammeExport.js->exportData->Error",
                error
            );
            if (onError) {
                onError(error);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        error?.response?.data?.message ||
                        "Unable to export linked programmes. Please try again.",
                });
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <StyledExportGroup aria-label="Export linked programmes">
            <StyledExportButton
                type="button"
                title="Export Excel"
                aria-label="Export Excel"
                disabled={isProcessing || isExporting}
                onClick={() =>
                    exportData(
                        "xlsx",
                        "PMC_Linked_Programmes"
                    )
                }
            >
                <i
                    className="fal fa-file-excel"
                    aria-hidden="true"
                />
            </StyledExportButton>

            <StyledExportButton
                type="button"
                title="Export CSV"
                aria-label="Export CSV"
                disabled={isProcessing || isExporting}
                onClick={() =>
                    exportData(
                        "csv",
                        "PMC_Linked_Programmes"
                    )
                }
            >
                <i
                    className="fal fa-file-csv"
                    aria-hidden="true"
                />
            </StyledExportButton>

            <StyledExportButton
                type="button"
                title="Export PDF"
                aria-label="Export PDF"
                disabled={isProcessing || isExporting}
                onClick={() =>
                    exportData(
                        "pdf",
                        "PMC_Linked_Programmes"
                    )
                }
            >
                <i
                    className="fal fa-file-pdf"
                    aria-hidden="true"
                />
            </StyledExportButton>
        </StyledExportGroup>
    );
}

export default LinkedProgrammeExport;
